import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateClickArg, DayCellMountArg, EventClickArg, EventMountArg } from '@fullcalendar/core';
import moment from 'moment';
import { Modal, Button } from 'react-bootstrap';
import type {
  JobTypeSummary,
  PositionTypeSummary,
  PracticeSummary,
  SkillSummary,
  TempRequestCalendarJob,
  TempRequestCalendarPayload,
  UserSummary,
} from '../../../shared/types/domainModels';

// Import the Jobs EmbedForm component for creating temp jobs
import { EmbedForm } from '../../jobs';

// Helper function to convert readable time (e.g., "8:00 AM") to 24-hour format for datetime construction
const convertTimeToISO = (date: string, timeStr: string): string => {
  if (!date || !timeStr) return '';

  // Parse time string like "8:00 AM" or "5:30 PM"
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return '';

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  // Convert to 24-hour format
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  const hoursStr = hours.toString().padStart(2, '0');
  return `${date}T${hoursStr}:${minutes}:00`;  // No Z suffix = local time
};

interface JobEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay?: boolean;
  color?: string;
  url?: string;
  extendedProps?: {
    description?: string;
    status?: string;
    temp_filled?: boolean;
  };
}

interface FormElements {
  [key: string]: unknown;
}

interface ScheduleProps {
  authenticity_token: string;
  jobs: TempRequestCalendarJob[];
  skills: SkillSummary[];
  practice_id: number;
  practices: PracticeSummary[];
  current_user: UserSummary;
  current_practice: PracticeSummary;
  can_see_all_practices?: boolean;
  can_see_other_practice_jobs?: boolean;
  // Additional props needed for EmbedForm
  all_skills: SkillSummary[];
  skill_categories: string[];
  job_types: JobTypeSummary[];
  form_elements: FormElements;
  position_types: PositionTypeSummary[];
  job_skills: SkillSummary[];
}

const Schedule: React.FC<ScheduleProps> = ({
  authenticity_token,
  jobs,
  skills,
  practice_id,
  practices,
  current_user,
  current_practice,
  can_see_all_practices = false,
  can_see_other_practice_jobs = false,
  all_skills,
  skill_categories,
  job_types,
  form_elements,
  position_types,
  job_skills
}) => {
  const [events, setEvents] = useState<JobEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [clickedDay, setClickedDay] = useState<string>('');
  const [successJob, setSuccessJob] = useState<TempRequestCalendarJob | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  // Calendar ref for accessing FullCalendar API
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    loadEvents();
  }, []);



  // Handle body scroll when modal is open/closed
  useEffect(() => {
    if (showForm) {
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [showForm]);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showForm) {
        handleFormClose();
      }
    };

    if (showForm) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showForm]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const [openResponse, filledResponse] = await Promise.all([
        fetch('/temps/open.json'),
        fetch('/temps/filled.json')
      ]);

      if (!openResponse.ok || !filledResponse.ok) {
        throw new Error('Failed to fetch events');
      }

      const [openJobs, filledJobs] = await Promise.all([
        openResponse.json(),
        filledResponse.json()
      ]);

      const normalizeTempJob = (
        item: TempRequestCalendarPayload | TempRequestCalendarJob
      ): TempRequestCalendarJob => {
        if ('temp' in item && item.temp) {
          return item.temp;
        }

        return item;
      };

      // Process open jobs - handle nested temp object structure from API
      const openEvents: JobEvent[] = (
        Array.isArray(openJobs)
          ? (openJobs as Array<TempRequestCalendarPayload | TempRequestCalendarJob>)
          : []
      )
        .map(normalizeTempJob)
        .filter((job) => {
          // Check for temp_start which is the date field from API
          return job.temp_start && moment(job.temp_start).isValid();
        })
        .map((job) => {
          // Use readable times if available (preferred for timezone accuracy), otherwise fall back to temp_start/end
          const useReadableTimes = job.temp_date && job.temp_start_time && job.temp_end_time;
          const start = useReadableTimes
            ? convertTimeToISO(job.temp_date, job.temp_start_time)
            : job.temp_start;
          const end = useReadableTimes
            ? convertTimeToISO(job.temp_date, job.temp_end_time)
            : (job.temp_end || job.temp_start);

          return {
            id: `open_${job.id}`,
            title: 'Temp Position - Open', // Generic title since API doesn't provide one
            start: start,
            end: end,
            allDay: false, // temp_start/end have time components
            color: job.color || '#0f4b89',
            url: job.job_url || `/jobs/${job.id}`, // Use job_url from API
            extendedProps: {
              status: 'open',
              temp_filled: false
            }
          };
        });

      // Process filled jobs - handle nested temp object structure from API
      const filledEvents: JobEvent[] = (
        Array.isArray(filledJobs)
          ? (filledJobs as Array<TempRequestCalendarPayload | TempRequestCalendarJob>)
          : []
      )
        .map(normalizeTempJob)
        .filter((job) => {
          // Check for temp_start which is the date field from API
          return job.temp_start && moment(job.temp_start).isValid();
        })
        .map((job) => {
          // Use readable times if available (preferred for timezone accuracy), otherwise fall back to temp_start/end
          const useReadableTimes = job.temp_date && job.temp_start_time && job.temp_end_time;
          const start = useReadableTimes
            ? convertTimeToISO(job.temp_date, job.temp_start_time)
            : job.temp_start;
          const end = useReadableTimes
            ? convertTimeToISO(job.temp_date, job.temp_end_time)
            : (job.temp_end || job.temp_start);

          return {
            id: `filled_${job.id}`,
            title: 'Temp Position - Filled', // Generic title since API doesn't provide one
            start: start,
            end: end,
            allDay: false, // temp_start/end have time components
            color: '#28a745', // Always use green for filled jobs
            url: job.job_url || `/jobs/${job.id}`, // Use job_url from API
            extendedProps: {
              status: 'filled',
              temp_filled: true
            }
          };
        });

      const allEvents = [...openEvents, ...filledEvents];
      setEvents(allEvents);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayClick = (info: DateClickArg) => {
    // Validate the date before setting it
    if (info.dateStr && moment(info.dateStr).isValid()) {
      setClickedDay(info.dateStr);
      setShowForm(true);
    } else {
      // Set a fallback date to prevent errors
      const fallbackDate = moment().format('YYYY-MM-DD');
      setClickedDay(fallbackDate);
      setShowForm(true);
    }
  };

  const handleEventClick = (info: EventClickArg) => {
    // Open the event URL in the same window
    if (info.event.url) {
      window.location.href = info.event.url;
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setClickedDay('');
  };

  const handleFormSuccess = (job?: TempRequestCalendarJob) => {
    // Redirect to job page to show matches after successful creation
    if (job && job.id) {
      window.location.href = `/jobs/${job.id}`;
    } else {
      // Fallback: reload events and close form if no job returned
      loadEvents();
      handleFormClose();
    }
  };

  // Create job object for the form
  const createJobForForm = () => {
    // Safely create the temp_date
    let temp_date;
    if (clickedDay && moment(clickedDay).isValid()) {
      temp_date = clickedDay;
    } else {
      temp_date = moment().format('YYYY-MM-DD');
    }

    const jobData = {
      temp_date,
      practice_id: current_practice?.id || practice_id,
      position_type: 'temp'
    };

    return jobData;
  };

  // Helper function to safely format clicked day for display
  const getFormattedClickedDay = () => {
    if (!clickedDay) return 'Selected Date';

    const momentDate = moment(clickedDay);
    if (!momentDate.isValid()) {
      return 'Selected Date';
    }

    return momentDate.format('MMMM Do, YYYY');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {showForm ? (
            <div className="row justify-content-center">
              <div className="col-12 col-md-6">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">
                      <i className="fa fa-plus-circle me-2"></i>
                      Create Temp Job for {getFormattedClickedDay()}
                    </h4>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light float-end"
                      onClick={handleFormClose}
                    >
                      <i className="fa fa-arrow-left me-1"></i>
                      Back to Calendar
                    </button>
                  </div>
                  <div className="card-body">
                    <EmbedForm
                      authenticity_token={authenticity_token}
                      practices={practices}
                      skills={all_skills || skills}
                      skill_categories={skill_categories}
                      job_types={job_types}
                      form_elements={form_elements}
                      position_types={position_types}
                      job_skills={job_skills}
                      job={createJobForForm()}
                      temp={true}
                      onSuccess={handleFormSuccess}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-0">
                  <i className="fa fa-calendar me-2"></i>
                  Temp Job Schedule
                </h4>
                <small className="text-white-50">Click on any date to create a new temp job</small>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <small className="text-muted me-2">Legend:</small>
                      <span className="badge bg-primary text-white me-2">
                        <i className="fa fa-circle me-1"></i>Open Jobs
                      </span>
                      <span className="badge bg-success text-white">
                        <i className="fa fa-circle me-1"></i>Filled Jobs
                      </span>
                    </div>
                    {loading && (
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const today = moment().format('YYYY-MM-DD');
                      setClickedDay(today);
                      setShowForm(true);
                    }}
                  >
                    <i className="fa fa-plus me-1"></i>
                    Create New Temp Job
                  </button>
                </div>

                <div className="calendar-wrapper">
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    timeZone="local"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,dayGridWeek'
                    }}
                    events={events}
                    dateClick={handleDayClick}
                    eventClick={handleEventClick}
                    height="auto"
                    dayMaxEvents={3}
                    moreLinkClick="popover"
                    navLinks={true}
                    eventDisplay="block"
                    displayEventTime={false}
                    eventTimeFormat={{
                      hour: 'numeric',
                      minute: '2-digit',
                      meridiem: 'short'
                    }}
                    eventDidMount={(info: EventMountArg) => {
                      // Add tooltips to events
                      const element = info.el;
                      const event = info.event;

                      element.setAttribute('data-bs-toggle', 'tooltip');
                      element.setAttribute('data-bs-placement', 'top');
                      element.setAttribute('title', `${event.title}\n${event.extendedProps.description || 'Click to view details'}`);

                      // Initialize Bootstrap tooltip
                      setTimeout(() => {
                        const bootstrapWindow = window as Window & {
                          bootstrap?: { Tooltip: new (targetElement: Element) => unknown };
                        };
                        if (bootstrapWindow.bootstrap?.Tooltip) {
                          new bootstrapWindow.bootstrap.Tooltip(element);
                        }
                      }, 100);
                    }}
                    dayCellDidMount={(info: DayCellMountArg) => {
                      // Add click affordance to empty days
                      const element = info.el;
                      element.setAttribute('title', 'Click to create a new temp job');
                      element.style.cursor = 'pointer';
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fa fa-check-circle me-2"></i>
            Temp Job Posted!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Your temp job has been successfully created and is now visible to professionals.</p>
          {successJob && (
            <div className="bg-light p-3 rounded">
              <p className="mb-2">
                <strong>Date:</strong> {moment(successJob.temp_date).format('MMMM Do, YYYY')}
              </p>
              <p className="mb-0">
                <strong>Position:</strong> {successJob.job_type?.title || 'Temp Position'}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
          {successJob && (
            <Button variant="primary" href={`/jobs/${successJob.id}`}>
              <i className="fa fa-eye me-1"></i>
              View Job
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Schedule;
